import { useTranslation } from 'react-i18next';

import { TableChartConfigDto, PitcherPitchByPitchStatsChartDataDto, PitchStats } from 'types/CodegenReportTypes';

import { ChartProps } from 'utilities/chartIdToComponent';
import { useMemo, useState } from 'react';
import Table, { Order } from 'components/Table/Table';
import { getDisplayedColumns, tableHeaders } from 'utilities/tableHeaders';
import PitchTypeTableCell from 'components/Table/PitchTypeTableCell';
import PitchLocationTableCell from 'components/Table/PitchLocationTableCell';
import useReportStore from 'hooks/PracticeReport/PitcherSingle/reportStore';

export type PracticePitcherSingleSessionPitchByPitchStatsChartColumn =
  keyof PitcherPitchByPitchStatsChartDataDto['pitchByPitchStats'][number];

const PracticePitcherSingleSessionPitchByPitchStatsChart = ({
  chartData,
  isLoading,
  chartConfig,
}: ChartProps<PitcherPitchByPitchStatsChartDataDto, TableChartConfigDto>) => {
  const { t } = useTranslation();

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<PracticePitcherSingleSessionPitchByPitchStatsChartColumn | '' | 'id'>('');

  const { selectedConfgValues } = useReportStore();

  const rows = useMemo(
    () =>
      chartData?.pitchByPitchStats.map((row, i) => ({
        ...row,
        id: i.toString(),
      })) ?? [],
    [chartData?.pitchByPitchStats]
  );

  const columns = useMemo(() => {
    const displayedColumns = getDisplayedColumns<PracticePitcherSingleSessionPitchByPitchStatsChartColumn>(chartConfig.visibleStats);
    const pitchTypeColumnNameKey = selectedConfgValues.pitchGrouping === 'Types' ? 'pitchType' : 'parentPitchType';

    return displayedColumns.map((column) => ({
      ...tableHeaders[column],
      field: column,
      headerName: column === 'pitchType' ? t(pitchTypeColumnNameKey) : t(column),
      headerShortName: column === 'pitchType' ? t('short.' + pitchTypeColumnNameKey) : t('short.' + column),
      sortable: true,
      renderCell:
        column === 'pitchCall'
          ? (cellValue) => t(cellValue)
          : column === 'pitchLocation'
            ? (cellValue, row: PitchStats) => (
                <PitchLocationTableCell pitchLocation={cellValue} pitchType={row.pitchType} playerType='Pitcher' />
              )
            : column === 'pitchType'
              ? (cellValue) => <PitchTypeTableCell pitchType={cellValue} />
              : undefined,
    }));
  }, [chartConfig.visibleStats, selectedConfgValues.pitchGrouping]);

  return (
    <Table
      rows={rows}
      pageState={[page, setPage]}
      orderState={[order, setOrder]}
      orderByState={[orderBy, setOrderBy]}
      columns={columns}
      loading={isLoading}
      noFooter
      tileTable
    />
  );
};

export default PracticePitcherSingleSessionPitchByPitchStatsChart;
